'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { ProjectCaseStudy, projects } from '@/data/projects';
import Link from 'next/link';
import SceneCanvas from '@/components/SceneCanvas';
import ArchitecturalGrid from '@/components/VisionaryScene';
import BlueprintOverlay from '@/components/BlueprintOverlay';
import BlueprintSchema from '@/components/BlueprintSchema';
import { ADRCard } from '@/components/ADRCard';
import { PullQuote } from '@/components/PullQuote';
import { HighlightedParagraph } from '@/components/HighlightedParagraph';
import { motion, AnimatePresence } from 'framer-motion';
interface ProjectCaseStudyClientProps {
  project: ProjectCaseStudy;
}
export default function ProjectCaseStudyClient(t0) {
  const $ = _c(136);
  const {
    project
  } = t0;
  const [isBlueprintMode, setIsBlueprintMode] = useState(false);
  const t1 = `fixed inset-0 z-0 transition-colors duration-1000 ${isBlueprintMode ? "bg-[#021a1a]" : "bg-black"}`;
  const t2 = isBlueprintMode ? 0.02 : 0.1;
  let t3;
  if ($[0] !== t2) {
    t3 = <ambientLight intensity={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [10, 10, 10];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = isBlueprintMode ? 0.2 : 1;
  const t6 = isBlueprintMode ? "#14b8a6" : "#6366f1";
  let t7;
  if ($[3] !== t5 || $[4] !== t6) {
    t7 = <pointLight position={t4} intensity={t5} color={t6} />;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== isBlueprintMode) {
    t8 = <ArchitecturalGrid isBlueprint={isBlueprintMode} />;
    $[6] = isBlueprintMode;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== isBlueprintMode || $[9] !== project.blueprint) {
    t9 = isBlueprintMode && <BlueprintSchema blueprint={project.blueprint} />;
    $[8] = isBlueprintMode;
    $[9] = project.blueprint;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t10 = <SceneCanvas>{t3}{t7}{t8}{t9}</SceneCanvas>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t1 || $[17] !== t10) {
    t11 = <div className={t1}>{t10}</div>;
    $[16] = t1;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isBlueprintMode || $[20] !== project) {
    t12 = isBlueprintMode && <BlueprintOverlay project={project} />;
    $[19] = isBlueprintMode;
    $[20] = project;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <AnimatePresence>{t12}</AnimatePresence>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isBlueprintMode) {
    t14 = () => setIsBlueprintMode(!isBlueprintMode);
    $[24] = isBlueprintMode;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = isBlueprintMode ? "Close Schema \u2014 Hide Architectural Blueprint" : "View Blueprint \u2014 Show Architectural Schema";
  const t16 = isBlueprintMode ? "Close Schema" : "View Blueprint";
  let t17;
  if ($[26] !== t16) {
    t17 = <span className="font-mono text-[9px] uppercase tracking-[0.4em] mb-4 text-zinc-500 group-hover:text-primary transition-colors">{t16}</span>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = `size-12 rounded-full border flex items-center justify-center transition-all duration-500 group-focus-visible:ring-2 group-focus-visible:ring-primary ${isBlueprintMode ? "border-primary bg-primary text-black" : "border-white/10 text-white hover:border-primary/50"}`;
  const t19 = isBlueprintMode ? 45 : 0;
  let t20;
  if ($[28] !== t19) {
    t20 = {
      rotate: t19
    };
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = isBlueprintMode ? "\xD7" : "+";
  let t22;
  if ($[30] !== t20 || $[31] !== t21) {
    t22 = <div className="relative size-6"><motion.div aria-hidden="true" animate={t20} className="absolute inset-0 flex items-center justify-center">{t21}</motion.div></div>;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t18 || $[34] !== t22) {
    t23 = <div className={t18}>{t22}</div>;
    $[33] = t18;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t17 || $[39] !== t23) {
    t24 = <button onClick={t14} className="fixed bottom-12 right-12 z-[100] flex flex-col items-end group focus-visible:outline-none" aria-label={t15}>{t17}{t23}</button>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t17;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const t25 = `relative z-10 max-w-6xl mx-auto transition-all duration-1000 ${isBlueprintMode ? "opacity-60 grayscale pointer-events-none" : "opacity-100"}`;
  let t26;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <nav className="mb-12"><Link href="/" className="inline-flex items-center gap-4 group focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-lg p-1" aria-label={"Return to Epochs \u2014 Back to career timeline"}><div aria-hidden="true" className="size-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-primary/50 transition-colors text-zinc-500 group-hover:text-primary">←</div><span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 group-hover:text-zinc-300 transition-colors">Return to Epochs</span></Link></nav>;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== project.slug) {
    t27 = p => p.slug === project.slug;
    $[42] = project.slug;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = projects.findIndex(t27) + 1;
  let t29;
  if ($[44] !== t28) {
    t29 = <span className="font-mono text-[11px] text-primary uppercase tracking-[0.4em]">Case Study / 0{t28}</span>;
    $[44] = t28;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  let t30;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="h-px w-12 bg-primary/30" />;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] !== project.period) {
    t31 = <span className="font-mono text-[11px] text-zinc-500 uppercase tracking-[0.4em]">{project.period}</span>;
    $[47] = project.period;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t29 || $[50] !== t31) {
    t32 = <div className="flex items-center gap-4 mb-6">{t29}{t30}{t31}</div>;
    $[49] = t29;
    $[50] = t31;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== project.title) {
    t33 = <h1 className="text-6xl md:text-9xl font-serif italic mb-8 leading-[0.9] tracking-tighter text-balance">{project.title}</h1>;
    $[52] = project.title;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== project.company) {
    t34 = <h2 className="text-2xl font-mono text-accent uppercase tracking-widest">{project.company}</h2>;
    $[54] = project.company;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== project.role) {
    t35 = <span className="text-zinc-600 font-serif italic text-xl">{project.role}</span>;
    $[56] = project.role;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== t34 || $[59] !== t35) {
    t36 = <div className="flex flex-wrap gap-8 items-baseline">{t34}{t35}</div>;
    $[58] = t34;
    $[59] = t35;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  let t37;
  if ($[61] !== t32 || $[62] !== t33 || $[63] !== t36) {
    t37 = <header className="mb-24">{t32}{t33}{t36}</header>;
    $[61] = t32;
    $[62] = t33;
    $[63] = t36;
    $[64] = t37;
  } else {
    t37 = $[64];
  }
  let t38;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <h3 className="text-sm font-mono text-primary uppercase tracking-[0.3em] mb-12 flex items-center gap-4">The Narrative <div className="h-px flex-grow bg-white/5" /></h3>;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-6 block">01 / Vision</span>;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] !== project.narrative.vision) {
    t40 = <div className="group">{t39}<PullQuote quote={project.narrative.vision} /></div>;
    $[67] = project.narrative.vision;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-4 block">02 / Execution</span>;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== project.narrative.execution) {
    t42 = project.narrative.execution.map(_temp);
    $[70] = project.narrative.execution;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== t42) {
    t43 = <div className="group">{t41}<div className="space-y-6">{t42}</div></div>;
    $[72] = t42;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  let t44;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-4 block">03 / Result</span>;
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  let t45;
  if ($[75] !== project.narrative.result) {
    t45 = project.narrative.result.map(_temp2);
    $[75] = project.narrative.result;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== t45) {
    t46 = <div className="group">{t44}<div className="space-y-6">{t45}</div></div>;
    $[77] = t45;
    $[78] = t46;
  } else {
    t46 = $[78];
  }
  let t47;
  if ($[79] !== t40 || $[80] !== t43 || $[81] !== t46) {
    t47 = <section>{t38}<div className="space-y-16">{t40}{t43}{t46}</div></section>;
    $[79] = t40;
    $[80] = t43;
    $[81] = t46;
    $[82] = t47;
  } else {
    t47 = $[82];
  }
  let t48;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <h3 className="text-sm font-mono text-primary uppercase tracking-[0.3em] mb-12 flex items-center gap-4">Strategic Decisions <div className="h-px flex-grow bg-white/5" /></h3>;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== project.adrs) {
    t49 = project.adrs.map(_temp3);
    $[84] = project.adrs;
    $[85] = t49;
  } else {
    t49 = $[85];
  }
  let t50;
  if ($[86] !== t49) {
    t50 = <section>{t48}<div className="space-y-12">{t49}</div></section>;
    $[86] = t49;
    $[87] = t50;
  } else {
    t50 = $[87];
  }
  let t51;
  if ($[88] !== t47 || $[89] !== t50) {
    t51 = <div className="lg:col-span-8 space-y-24">{t47}{t50}</div>;
    $[88] = t47;
    $[89] = t50;
    $[90] = t51;
  } else {
    t51 = $[90];
  }
  let t52;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <h3 className="text-xs font-mono text-primary uppercase tracking-widest mb-8">Strategic Impact</h3>;
    $[91] = t52;
  } else {
    t52 = $[91];
  }
  let t53;
  if ($[92] !== project.highlights) {
    t53 = project.highlights.map(_temp4);
    $[92] = project.highlights;
    $[93] = t53;
  } else {
    t53 = $[93];
  }
  let t54;
  if ($[94] !== t53) {
    t54 = <section className="glass p-8 rounded-[2rem] border-primary/10">{t52}<div className="space-y-8">{t53}</div></section>;
    $[94] = t53;
    $[95] = t54;
  } else {
    t54 = $[95];
  }
  let t55;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <h3 className="text-xs font-mono text-primary uppercase tracking-widest mb-8">Technical Ecosystem</h3>;
    $[96] = t55;
  } else {
    t55 = $[96];
  }
  let t56;
  if ($[97] !== project.techStack) {
    t56 = project.techStack.map(_temp5);
    $[97] = project.techStack;
    $[98] = t56;
  } else {
    t56 = $[98];
  }
  let t57;
  if ($[99] !== t56) {
    t57 = <section>{t55}<div className="flex flex-wrap gap-2">{t56}</div></section>;
    $[99] = t56;
    $[100] = t57;
  } else {
    t57 = $[100];
  }
  let t58;
  if ($[101] !== t54 || $[102] !== t57) {
    t58 = <div className="lg:col-span-4 space-y-16">{t54}{t57}</div>;
    $[101] = t54;
    $[102] = t57;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  let t59;
  if ($[104] !== t51 || $[105] !== t58) {
    t59 = <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">{t51}{t58}</div>;
    $[104] = t51;
    $[105] = t58;
    $[106] = t59;
  } else {
    t59 = $[106];
  }
  const currentIndex = projects.findIndex(p_0 => p_0.slug === project.slug);
  const nextProject = projects[(currentIndex + 1) % projects.length];
  let t60;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">Next Case Study</span>;
    $[107] = t60;
  } else {
    t60 = $[107];
  }
  const t61 = `/work/${nextProject.slug}`;
  let t62;
  if ($[108] !== nextProject.company) {
    t62 = <p className="font-mono text-[11px] uppercase tracking-widest text-primary mb-1">{nextProject.company}</p>;
    $[108] = nextProject.company;
    $[109] = t62;
  } else {
    t62 = $[109];
  }
  let t63;
  if ($[110] !== nextProject.title) {
    t63 = <p className="text-xl font-serif italic text-zinc-300 group-hover:text-white transition-colors">{nextProject.title}</p>;
    $[110] = nextProject.title;
    $[111] = t63;
  } else {
    t63 = $[111];
  }
  let t64;
  if ($[112] !== t62 || $[113] !== t63) {
    t64 = <div className="text-right">{t62}{t63}</div>;
    $[112] = t62;
    $[113] = t63;
    $[114] = t64;
  } else {
    t64 = $[114];
  }
  let t65;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <div aria-hidden="true" className="size-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-primary/50 transition-colors text-zinc-400 group-hover:text-primary flex-shrink-0">→</div>;
    $[115] = t65;
  } else {
    t65 = $[115];
  }
  let t66;
  if ($[116] !== t61 || $[117] !== t64) {
    t66 = <div className="mt-32 pt-16 border-t border-white/5 flex items-center justify-between">{t60}<Link href={t61} className="group inline-flex items-center gap-4 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-lg p-1">{t64}{t65}</Link></div>;
    $[116] = t61;
    $[117] = t64;
    $[118] = t66;
  } else {
    t66 = $[118];
  }
  const t67 = t66;
  let t68;
  let t69;
  let t70;
  let t71;
  let t72;
  let t73;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <Link href="/about" className="hover:text-zinc-300 transition-colors">About</Link>;
    t69 = <span className="mx-3">·</span>;
    t70 = <Link href="/contact" className="hover:text-zinc-300 transition-colors">Contact</Link>;
    t71 = <span className="mx-3">·</span>;
    t72 = <Link href="/privacy" className="hover:text-zinc-300 transition-colors">Privacy Policy</Link>;
    t73 = <span className="mx-3">·</span>;
    $[119] = t68;
    $[120] = t69;
    $[121] = t70;
    $[122] = t71;
    $[123] = t72;
    $[124] = t73;
  } else {
    t68 = $[119];
    t69 = $[120];
    t70 = $[121];
    t71 = $[122];
    t72 = $[123];
    t73 = $[124];
  }
  let t74;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <p className="mt-12 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-700 text-center">{t68}{t69}{t70}{t71}{t72}{t73}<span>© {new Date().getFullYear()} Wenceslaus Dsilva</span></p>;
    $[125] = t74;
  } else {
    t74 = $[125];
  }
  let t75;
  if ($[126] !== t25 || $[127] !== t37 || $[128] !== t59 || $[129] !== t67) {
    t75 = <div className={t25}>{t26}{t37}{t59}{t67}{t74}</div>;
    $[126] = t25;
    $[127] = t37;
    $[128] = t59;
    $[129] = t67;
    $[130] = t75;
  } else {
    t75 = $[130];
  }
  let t76;
  if ($[131] !== t11 || $[132] !== t13 || $[133] !== t24 || $[134] !== t75) {
    t76 = <main className="relative min-h-screen text-white pt-32 pb-20 px-8 md:px-24 selection:bg-primary/30">{t11}{t13}{t24}{t75}</main>;
    $[131] = t11;
    $[132] = t13;
    $[133] = t24;
    $[134] = t75;
    $[135] = t76;
  } else {
    t76 = $[135];
  }
  return t76;
}
function _temp5(tech) {
  return <span key={tech} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-mono text-zinc-400 tracking-widest">{tech}</span>;
}
function _temp4(h, i_2) {
  return <div key={i_2} className="border-b border-white/5 pb-6 last:border-0"><span className="text-4xl md:text-5xl font-serif italic text-white block mb-1">{h.value}</span><span className="text-xs font-mono text-zinc-500 uppercase tracking-widest">{h.label}</span></div>;
}
function _temp3(adr, i_1) {
  return <ADRCard key={i_1} adr={adr} index={i_1} />;
}
function _temp2(para_0, i_0) {
  return <HighlightedParagraph key={i_0} text={para_0} className="text-lg md:text-xl text-zinc-400 font-sans leading-loose text-justify hyphens-auto" />;
}
function _temp(para, i) {
  return <HighlightedParagraph key={i} text={para} className="text-lg md:text-xl text-zinc-400 font-sans leading-loose text-justify hyphens-auto" />;
}